import { Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import type {
  AiAccessPromptView,
  AiGuardNoticeView,
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisRunView,
} from "@/features/ai";
import type { StructuralAnalysisHistoryEntryView } from "@/features/ai/ai.types";
import { useRuntimeCreditCost } from "@/features/pricing/useRuntimePricing";
import type { StructuralAnalysisAvailability } from "@/features/story";
import { StructuralAnalysisHistoryList } from "./structural-analysis-history-list";
import { StructuralAnalysisReport } from "./structural-analysis-report";

type StructuralAnalysisDialogProps = {
  activeRunId: string | null;
  availability: StructuralAnalysisAvailability;
  busy?: boolean;
  deleteBusy?: boolean;
  errorMessage?: string | null;
  guardNotices?: AiGuardNoticeView[];
  history: StructuralAnalysisHistoryEntryView[];
  onDeleteRun?: (runId: string) => void;
  onOpenChange: (open: boolean) => void;
  onOpenChapter?: (
    callout: StructuralAnalysisRunView["sections"][number]["findings"][number],
  ) => void;
  onOpenRun: (runId: string) => void;
  onPromptAction?: () => void;
  onRunAnalysis?: () => void;
  onSetReviewState: (
    runId: string,
    dimensionName: StructuralAnalysisRunView["sections"][number]["dimension"],
    findingIndex: number,
    reviewState: StructuralAnalysisCalloutReviewState,
  ) => void;
  open: boolean;
  prompt?: AiAccessPromptView | null;
  run: StructuralAnalysisRunView | null;
  statusBusyId?: string | null;
  statusLabel?: string | null;
  storyTitle: string;
};

export function StructuralAnalysisDialog({
  activeRunId,
  availability,
  busy = false,
  deleteBusy = false,
  errorMessage = null,
  guardNotices = [],
  history,
  onDeleteRun,
  onOpenChange,
  onOpenChapter,
  onOpenRun,
  onPromptAction,
  onRunAnalysis,
  onSetReviewState,
  open,
  prompt = null,
  run,
  statusBusyId = null,
  statusLabel = null,
  storyTitle,
}: StructuralAnalysisDialogProps) {
  const { t } = useTranslation();
  const structuralAnalysisCreditCost = useRuntimeCreditCost("structural_analysis");

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] w-[min(96vw,88rem)] gap-0 overflow-hidden border-[#eadcc7] bg-[#fffdf9] p-0 shadow-[0_28px_90px_-36px_rgba(56,40,22,0.28)] backdrop-blur-none">
        <DialogHeader className="border-b border-ink/10 px-8 pb-6 pt-8 pr-16">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
            {t("story.workspace.aiWorkspace", { defaultValue: "AI workspace" })}
          </p>
          <DialogTitle>{t("story.ai.structural.title")}</DialogTitle>
          <DialogDescription className="max-w-4xl text-base leading-7 text-ink/70">
            {t("story.ai.structural.body")}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(90vh-13rem)] overflow-y-auto">
          <div className="divide-y divide-[#ece2d6]">
            {(errorMessage || prompt || !availability.eligible) ? (
              <section className="space-y-4 px-8 py-6">
                {errorMessage ? (
                  <div
                    className="rounded-[1.2rem] border border-destructive/25 bg-destructive/5 px-4 py-4 text-sm text-destructive"
                    role="alert"
                  >
                    {errorMessage}
                  </div>
                ) : null}

                {prompt ? (
                  <div className="space-y-3 rounded-[1.2rem] border border-amber-300/40 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
                    <p>{t(prompt.descriptionKey, prompt.descriptionValues)}</p>
                    {prompt.ctaLabelKey && onPromptAction ? (
                      <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
                        {t(prompt.ctaLabelKey)}
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                <AiGuardNoticeList notices={guardNotices} />

                {!availability.eligible ? (
                  <div className="rounded-[1.2rem] border border-[#e7d9c8] bg-[#f8f2e9] px-4 py-4 text-sm leading-6 text-muted-foreground">
                    {availability.reason}
                  </div>
                ) : null}
              </section>
            ) : null}

            <section className="grid gap-0 xl:grid-cols-[22rem_minmax(0,1fr)]">
              <div className="border-b border-[#ece2d6] bg-[#fbf6ee]/45 px-8 py-6 xl:border-b-0 xl:border-r">
                <div className="space-y-1 pb-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    {t("story.ai.structural.history.title", {
                      defaultValue: "Saved analyses",
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("story.ai.structural.history.subtitle", {
                      defaultValue:
                        "Switch between saved runs without leaving the chapter workspace.",
                    })}
                  </p>
                </div>
                <StructuralAnalysisHistoryList
                  activeRunId={activeRunId}
                  history={history}
                  onOpenRun={onOpenRun}
                />
              </div>

              <div className="px-8 py-6">
                <StructuralAnalysisReport
                  deleteBusy={deleteBusy}
                  onDeleteRun={onDeleteRun}
                  onOpenChapter={onOpenChapter}
                  onSetReviewState={onSetReviewState}
                  run={run}
                  statusBusyId={statusBusyId}
                  storyTitle={storyTitle}
                />
              </div>
            </section>
          </div>
        </div>

        <DialogFooter className="border-t border-[#ece2d6] px-8 py-5 sm:justify-between">
          <p className="text-sm text-ink/60">
            {statusLabel ?? t("story.ai.structural.creditStatus", {
              cost: structuralAnalysisCreditCost,
              remaining: 0,
            })}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              {t("common.close")}
            </Button>
            <Button
              disabled={busy || !availability.eligible}
              onClick={onRunAnalysis}
              type="button"
              variant="outline"
            >
              <Activity className="h-4 w-4" />
              {busy ? t("story.ai.structural.running") : t("story.ai.structural.runAction")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
